import type { ID } from '@octopus-do/octopus-project-core'
import type React from 'react'

import {
  BlockNotes,
  Blocks,
  Comments,
  Notes,
  SearchEntity,
  type SearchResult,
  useOnSelect,
  useOpenContent,
} from '../../../Search'
import css from './styles.module.css'

interface Props {
  result: SearchResult
}

// Everything the search matches that is not a page. Pages are the tree above,
// so this renders the remaining entity lists directly rather than through
// SearchResults, which would run its own search pass and print its own "No
// matches" next to a tree that does have matches.
//
// Mounted only while there is something to show, on purpose: useOnSelect and
// useOpenContent subscribe to the whole store (useOpenContent literally selects
// the root state), so keeping them alive would re-render the page tree on every
// unrelated dispatch.
export const ContentResults: React.FC<Props> = ({ result }) => {
  const onResultClick = useOnSelect()
  const openBlockContent = useOpenContent('block')

  const handleClickContent = (entity: SearchEntity, payload: { id: ID }) => {
    if (entity === SearchEntity.BLOCK) {
      openBlockContent(payload.id)
    }
  }

  return (
    <div className={css.results}>
      {Object.keys(result[SearchEntity.BLOCK]).length > 0 && (
        <Blocks
          blocks={result[SearchEntity.BLOCK]}
          selectedItemId={null}
          contentButton
          onClick={onResultClick}
          onClickContent={handleClickContent}
        />
      )}

      {Object.keys(result[SearchEntity.BLOCK_NOTE]).length > 0 && (
        <BlockNotes
          blocks={result[SearchEntity.BLOCK_NOTE]}
          selectedItemId={null}
          onClick={onResultClick}
        />
      )}

      {Object.keys(result[SearchEntity.NOTE]).length > 0 && (
        <Notes
          notes={result[SearchEntity.NOTE]}
          selectedItemId={null}
          onClick={onResultClick}
        />
      )}

      {Object.keys(result[SearchEntity.COMMENT]).length > 0 && (
        <Comments
          comments={result[SearchEntity.COMMENT]}
          selectedItemId={null}
          onClick={onResultClick}
        />
      )}
    </div>
  )
}
